import { fetchWrapper } from 'helpers/helpers';
import type { PaginatedResponse, Uuid } from 'store/reducers/types';
import type { PromotionRequest } from 'store/reducers/promotionRequestReducer';

export const getJobBoards = (): Promise<string[] | null | undefined> =>
  fetchWrapper<string[]>({
    url: 'promotion-requests/options/job-boards/',
    method: 'GET',
  });

export interface PromotionRequestCreatePayload {
  openings: Uuid[];
  opening_order?: Uuid[];
  prior_opening?: Uuid | null;
  promotion_source: string;
  platform?: string[];
  job_boards?: string[];
  promotion_period_type: string;
  duration: number;
  request_title: string;
  description: string;
  search_regions: Uuid[];
  technology_areas: Uuid[];
  recruiter_leads: Uuid[];
  team_lead_notification_recipients?: Uuid[];
  translation_languages?: string[];
  additional_requirements?: string;
}

export interface PromotionRequestPublicationPayload {
  publication_start_date?: string | null;
  publication_links?: string[];
  publication_comment?: string;
}

export type PromotionRequestUpdatePayload = Partial<PromotionRequestCreatePayload> & PromotionRequestPublicationPayload;

export const createPromotionRequest = (
  jobId: Uuid,
  body: PromotionRequestCreatePayload
): Promise<PromotionRequest | null | undefined> =>
  fetchWrapper<PromotionRequest>({
    url: `jobs/${jobId}/promotion-requests/`,
    method: 'POST',
    body,
  });

export const getPromotionRequest = (
  jobId: Uuid,
  requestId: Uuid,
  { showErrorNotification = true }: { showErrorNotification?: boolean } = {}
): Promise<PromotionRequest | null | undefined> =>
  fetchWrapper<PromotionRequest>({
    url: `jobs/${jobId}/promotion-requests/${requestId}/`,
    method: 'GET',
    key: 'promotion-request-details',
    showErrorNotification,
  });

export const updatePromotionRequest = (
  jobId: Uuid,
  requestId: Uuid,
  body: PromotionRequestUpdatePayload
): Promise<PromotionRequest | null | undefined> =>
  fetchWrapper<PromotionRequest>({
    url: `jobs/${jobId}/promotion-requests/${requestId}/`,
    method: 'PATCH',
    body,
  });

export const copyPromotionRequest = (
  jobId: Uuid,
  requestId: Uuid,
  body: PromotionRequestCreatePayload
): Promise<PromotionRequest | null | undefined> =>
  fetchWrapper<PromotionRequest>({
    url: `jobs/${jobId}/promotion-requests/${requestId}/copy/`,
    method: 'POST',
    body,
  });

export const approvePromotionRequest = (jobId: Uuid, requestId: Uuid): Promise<PromotionRequest | null | undefined> =>
  fetchWrapper<PromotionRequest>({
    url: `jobs/${jobId}/promotion-requests/${requestId}/approve/`,
    method: 'PUT',
    body: {},
  });

export const declinePromotionRequest = (
  jobId: Uuid,
  requestId: Uuid,
  body: { decline_reason?: string }
): Promise<PromotionRequest | null | undefined> =>
  fetchWrapper<PromotionRequest>({
    url: `jobs/${jobId}/promotion-requests/${requestId}/decline/`,
    method: 'PUT',
    body,
  });

export const cancelPromotionRequest = (
  jobId: Uuid,
  requestId: Uuid,
  body: { cancellation_comment?: string }
): Promise<PromotionRequest | null | undefined> =>
  fetchWrapper<PromotionRequest>({
    url: `jobs/${jobId}/promotion-requests/${requestId}/cancel/`,
    method: 'PUT',
    body,
  });

export const closePromotionRequest = (jobId: Uuid, requestId: Uuid): Promise<PromotionRequest | null | undefined> =>
  fetchWrapper<PromotionRequest>({
    url: `jobs/${jobId}/promotion-requests/${requestId}/close/`,
    method: 'PUT',
    body: {},
  });

export interface PromotionRequestListOpening {
  uuid: Uuid;
  level: string;
  custom_level: string | null;
  status: string;
  is_prior?: boolean;
}

export interface PromotionRequestLead {
  uuid: Uuid;
  full_name: string;
  avatar: string | null;
}

export interface PromotionRequestListItem {
  uuid: Uuid;
  recruiter_leads: PromotionRequestLead[];
  request_title: string;
  promotion_source: string;
  status: string;
  openings_count: number;
  openings: PromotionRequestListOpening[];
  created_at: string;
  job: { uuid: Uuid; name: string };
}

export const getPromotionRequestsOnJob = (
  jobId: Uuid,
  params: Record<string, unknown>,
  { showErrorNotification = true }: { showErrorNotification?: boolean } = {}
) =>
  fetchWrapper<PaginatedResponse<PromotionRequestListItem>>({
    url: `jobs/${jobId}/promotion-requests-on-job/`,
    body: params,
    key: 'promotion-requests-on-job',
    showErrorNotification,
  });

export const getPromotionRequestsOnDashboardHRPR = (params: Record<string, unknown>) =>
  fetchWrapper<PaginatedResponse<PromotionRequestListItem>>({
    url: `dashboard/hrpr/promotion-requests/`,
    version: 'v2',
    body: params,
    key: 'promotion-requests-all',
  });

export const getPromotionRequestsOnDashboardRecruiterLead = (params: Record<string, unknown>) =>
  fetchWrapper<PaginatedResponse<PromotionRequestListItem>>({
    url: `dashboard/recruiter-lead/promotion-requests/`,
    version: 'v2',
    body: params,
    key: 'promotion-requests-recruiter-lead',
  });

export const getPromotionRequestsOnDashboardRecruiter = (params: Record<string, unknown>) =>
  fetchWrapper<PaginatedResponse<PromotionRequestListItem>>({
    url: `dashboard/recruiter/promotion-requests/`,
    version: 'v2',
    body: params,
    key: 'promotion-requests-recruiter',
  });
